import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { cloneDeep } from 'lodash'
import { Badge, Checkbox, Col, Divider, Empty, message, Radio, Row, Space, Tabs } from 'antd'
import { PrimaryButton } from 'Components/button'
import { isFormError, formValidation, validatePasswordFormat } from 'helpers/format'
import { selectCurrentUser } from 'Store/user/selector'
import { FormItem, Input } from 'Components/form'
import EventModal from 'Components/modal/EventModal'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { clearEventAction } from 'Store/manage-user/actions'
import theme, { MyTheme } from 'Components/common/Theme'
import InputPassword from 'Components/form/InputPassword'
import { CreateUserReq, MerchantRolesData, UpdateUserReq, UserDetailReq } from 'Store/manage-user/types'
import { passwordFormatConfig, permissionGroup } from 'helpers/constanst'
import { merchantCreateUserAPI, updateUserAPI, userDetailAPI } from 'Store/manage-user/api'
import { selectActionEvent, selectUserDetail } from 'Store/manage-user/selector'
const { TabPane } = Tabs

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  mode: any
  id?: number
  roles: MerchantRolesData[]
}

const UserDetail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    mode,
    userDetail,
    roles = [],
  } = props
  const detail = userDetail?.data
  const detailEvent = userDetail.event

  const isCreateMode = mode === 'create'
  const requireFeild = ['username']
  const initForm = {
    username: '',
    password: '',
    merchant_roles: [],
  } as CreateUserReq
  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [formError, setFormError] = useState([] as string[])
  const [password, setPassword] = useState<string>('')
  const [activeTab, setActiveTab] = useState('1')
  const [activeMerchant, setActiveMerchant] = useState<number>(0)
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [passwordValidKey, setPasswordValidKey] = useState<string[]>([])
  const dispatch = useDispatch()

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)
    if (isCreateMode) {
      if (formData.username.length < 6) {
        error.push('username')
      }

      if (passwordValidKey.length < 5) {
        error.push('password')
      }

      if (confirmPassword !== password) {
        error.push('confirmPassword')
      }
    }

    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const onCreate = (): void => {
    if (!validateForm()) {
      message.error('กรุณากรอกข้อมูลผู้ใช้ให้ครบถ้วน')
      return
    } else {
      if (formData.merchant_roles.length <= 0) {
        message.error('กรุณากำหนดสิทธิ์การใช้งานให้แก่ผู้ใช้')
        return
      }
    }
    const req: CreateUserReq = {
      ...formData,
      password: password,
    }
    dispatch(merchantCreateUserAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.id || 0))
  }

  const onGetDetail = (): void => {
    const req: UserDetailReq = {
      id,
    }
    dispatch(userDetailAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.id || 0))
  }

  const onUpdate = (): void => {
    if (!validateForm()) return
    if (formData.merchant_roles.length <= 0) {
      message.error('กรุณากำหนดสิทธิ์การใช้งานให้แก่ผู้ใช้')
      return
    }
    const req: UpdateUserReq = {
      ...formData,
      id,
    }
    dispatch(updateUserAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.id || 0))
  }

  useEffect(() => {
    if (visible && !isCreateMode && id > 0) {
      onGetDetail()
      setActiveTab('2')
    }

    if (isCreateMode) {
      setActiveTab('1')
    }

    setActiveMerchant(0)
    setPasswordValidKey([])
    setConfirmPassword('')
    setPassword('')
    setFormData(cloneDeep(initForm))
    setFormError([])
  }, [visible])

  useEffect(() => {
    const validKey = validatePasswordFormat(password)
    setPasswordValidKey(validKey)
  }, [password])

  useEffect(() => {
    if (mode === 'edit' && id > 0) {
      const roles = [] as any
      if (detail && detail) {
        detail?.merchant_roles.forEach(item => {
          roles.push({
            id: item.id,
            roles: item.role_ids || [],
          })
        })
      }
      const form = {
        username: detail?.username || '',
        merchant_roles: roles,
        //password: '',
      } as CreateUserReq
      setFormData({ ...form })
    }
  }, [detail])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [formData])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [confirmPassword])

  const onUpdateData = (val: any, key: string) => {
    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const title = isCreateMode ? 'สร้างผู้ใช้งาน' : `แก้ไขสิทธิ์ผู้ใช้งาน (${formData.username})`

  const FooterContent = (
    <Space size="middle" align="baseline">
      <PrimaryButton text="ยกเลิก" btnType="Cancel" onClick={onCloseDrawer} />
      {isCreateMode ? (
        <PrimaryButton text="ยืนยันการสร้าง" onClick={onCreate} />
      ) : (
        <PrimaryButton text="ยืนยันการแก้ไขข้อมูล" onClick={onUpdate} />
      )}
    </Space>
  )

  const onUpdatePassword = (val: string): void => {
    setPassword(val)
    if (formError.length > 0) {
      validateForm()
    }
  }

  const onUpdateConfirmPassword = (val: string): void => {
    setConfirmPassword(val)
    if (formError.length > 0) {
      validateForm()
    }
  }

  const isCheckMerchant = (mid: number) => {
    const check = formData.merchant_roles.find(item => item.id === mid)
    return check ? true : false
  }

  const removeMerchantID = (mid: number) => {
    const merchantRoles = formData.merchant_roles.filter(item => item.id !== mid)
    return merchantRoles
  }

  const onCheck = (value: boolean, mid: number) => {
    let newSelect = formData.merchant_roles
    if (isCheckMerchant(mid)) {
      const roles = removeMerchantID(mid)
      newSelect = roles
      setActiveMerchant((newSelect && newSelect[0]?.id) || 0)
    } else {
      newSelect.push({
        id: mid,
        roles: [],
      })
      setActiveMerchant(mid)
    }
    setFormData({
      merchant_roles: newSelect,
      password: formData.password,
      username: formData.username,
    })
  }

  const getRoleInfo = (mid: number) => {
    const role = roles.find(item => item.id === mid)
    return role?.roles || []
  }

  const isCheckRole = (mid: number, id: number) => {
    const roles = getSelectedRole(mid)
    const check = roles.find(item => item === id)
    return check ? true : false
  }

  const removeRoleID = (roles: number[], id: number) => {
    return roles.filter(item => item !== id)
  }

  const getSelectedRole = (mid: number) => {
    const merchant = formData.merchant_roles.find(item => item.id === mid)
    return merchant?.roles || []
  }

  const onCheckRole = (mid: number, roleID: number) => {
    const newSelect = cloneDeep(formData.merchant_roles)
    const merchant = newSelect.find(item => item.id === mid)
    let role = merchant?.roles || []
    if (!merchant) return
    if (merchant && isCheckRole(mid, roleID)) {
      const roles = removeRoleID(role, roleID)
      role = roles
    } else {
      role.push(roleID)
    }
    newSelect.map(item => {
      if (item.id === mid) {
        item.roles = role
      }
      return item
    })
    setFormData({
      merchant_roles: newSelect,
      password: formData.password,
      username: formData.username,
    })
  }

  const onSelectAllRole = (mid: number) => {
    const roleInfo = (isCheckMerchant(mid) && getRoleInfo(mid)) || []
    const newSelect = cloneDeep(formData.merchant_roles)
    const merchant = newSelect.find(item => item.id === mid)
    if (!merchant) return
    if (isSelectRoleAll(mid)) {
      merchant.roles = []
    } else {
      merchant.roles = []
      roleInfo.forEach(item => {
        merchant?.roles.push(item.id)
      })
    }
    newSelect.map(item => {
      if (item.id === mid) {
        return merchant
      } else {
        return item
      }
    })
    setFormData({
      merchant_roles: newSelect,
      password: formData.password,
      username: formData.username,
    })
  }

  const isSelectRoleAll = (mid: number) => {
    const roleInfo = (isCheckMerchant(mid) && getRoleInfo(mid)) || []
    let selectAll = true
    roleInfo.forEach(item => {
      if (!isCheckRole(mid, item.id)) {
        selectAll = false
        return
      }
    })
    return selectAll
  }

  const roleInfo = (isCheckMerchant(activeMerchant) && getRoleInfo(activeMerchant)) || []
  const selectedRole = getSelectedRole(activeMerchant)

  const renderCreateForm = () => {
    return (
      <Row justify="space-between" gutter={[16, 8]}>
        <Col lg={24} xs={24}>
          <FormItem label="" status={isError('username')} error="กรุณาระบุชื่อผู้ใช้งานอย่างน้อย 6 ตัวอักษร">
            <BoxTitle>ชื่อผู้ใช้งาน</BoxTitle>
            <Input
              placeholder="ชื่อผู้ใช้งาน"
              name="username"
              value={formData.username}
              onChange={onUpdateData}
              disabled={!isCreateMode}
              key="staff-username"
            />
          </FormItem>
        </Col>
        <Col lg={24} xs={24}>
          <BoxTitle>รหัสผ่าน</BoxTitle>
          <FormItem label="" status={isError('password')} error="รูปแบบของรหัสผ่านไม่ถูกต้อง">
            <InputPassword
              type="password"
              key="staff-password"
              value={password}
              placeholder="รหัสผ่าน"
              onChange={onUpdatePassword}
              resProps={{
                autoComplete: 'new-password',
              }}
            />
          </FormItem>
        </Col>
        <Col lg={24} xs={24}>
          <BoxTitle>ยืนยันรหัสผ่านอีกครั้ง</BoxTitle>
          <FormItem label="" status={isError('confirmPassword')} error="รหัสผ่านไม่ตรงกัน">
            <InputPassword
              type="password"
              value={confirmPassword}
              placeholder="ยืนยันรหัสผ่าน"
              onChange={onUpdateConfirmPassword}
            />
          </FormItem>
        </Col>
        <Col lg={24} xs={24}>
          <Description>
            <Space direction="vertical" size={'small'}>
              รหัสผ่านประกอบไปด้วย
              {passwordFormatConfig.map((item, index) => {
                return (
                  <CircleCheckBox checked={passwordValidKey.includes(item.value) ? true : false} key={index}>
                    {item.name}
                  </CircleCheckBox>
                )
              })}
            </Space>
          </Description>
        </Col>
        <Col lg={24} xs={24}>
          <Divider />
        </Col>
      </Row>
    )
  }

  const renderPermission = () => {
    return (
      <Row justify="space-between" gutter={[1, 8]}>
        <Col md={12} xs={24}>
          <Title>ร้านค้าที่ใช้งาน</Title>
          <Description>
            <div>
              <span>กรุณาเลือก “ร้านค้า”</span>
            </div>
            <br />
            {/* <Checkbox.Group style={{ marginTop: '20px' }}> */}
            <Row gutter={[8, 8]}>
              {roles.map((item, index) => {
                const isChecked = isCheckMerchant(item.id)
                return (
                  <Col span={22} key={index} onClick={() => isChecked && setActiveMerchant(item.id)}>
                    <MerchantItem className={item.id == activeMerchant ? 'active' : ''}>
                      <Row justify="space-between">
                        <Col span={20}>
                          <Checkbox
                            value={item.id}
                            checked={isCheckMerchant(item.id)}
                            onChange={e => onCheck(e.target.checked, item.id)}
                          ></Checkbox>{' '}
                          &nbsp;&nbsp;
                          <SpanMerchantName className={isChecked ? 'checked' : 'not-checked'}>
                            {item.name}
                          </SpanMerchantName>
                        </Col>
                        <Col>
                          {isChecked && (
                            <SpanCount
                              count={getSelectedRole(item.id).length}
                              style={{
                                backgroundColor: theme.colors.primary,
                              }}
                            />
                          )}
                        </Col>
                      </Row>
                    </MerchantItem>
                  </Col>
                )
              })}
            </Row>
            {/* </Checkbox.Group> */}
          </Description>
        </Col>
        <Col md={12} xs={24}>
          <Row justify="space-between">
            <Col>
              <Title>สิทธิ์การใช้งาน ({selectedRole.length})</Title>
            </Col>
            {roleInfo.length > 0 && (
              <Col>
                <SpanMerchantName onClick={() => onSelectAllRole(activeMerchant)}>
                  <Radio value={2} checked={isSelectRoleAll(activeMerchant)}></Radio>
                  เลือกทั้งหมด
                </SpanMerchantName>
              </Col>
            )}
          </Row>
          {roleInfo.length > 0 ? (
            <PermissionBoxWrapper>
              <Row gutter={[16, 24]}>
                {permissionGroup.map((group, index) => {
                  let hasChild = false
                  roleInfo.forEach(role => {
                    if (group.value.includes(role.name)) {
                      hasChild = true
                      return
                    }
                  })
                  if (!hasChild) return
                  return (
                    <Col span={24} key={index}>
                      <GroupTitle>{group.name}</GroupTitle>
                      <Row gutter={[16, 12]}>
                        {roleInfo.map((item, index) => {
                          if (!group.value.includes(item.name)) return
                          return (
                            <Col span={24} key={index}>
                              <Row justify="space-between">
                                <Col offset={1}>
                                  <Checkbox
                                    value={item.id}
                                    checked={isCheckRole(activeMerchant, item.id)}
                                    onChange={() => onCheckRole(activeMerchant, item.id)}
                                  ></Checkbox>
                                  &nbsp;<PermissionName>{item.name}</PermissionName>
                                </Col>
                              </Row>
                            </Col>
                          )
                        })}
                      </Row>
                    </Col>
                  )
                })}
              </Row>
            </PermissionBoxWrapper>
          ) : (
            <Empty description="กรุณาเลือกร้านค้า" />
          )}
        </Col>
      </Row>
    )
  }

  const getTab = () => {
    return (
      <StickyHeader>
        <Tabs onChange={setActiveTab} type="line" activeKey={activeTab}>
          {isCreateMode && (
            <TabPane tab={'ข้อมูลผู้ใช้งาน'} key="1">
              {renderCreateForm()}
            </TabPane>
          )}
          <TabPane tab={'กำหนดสิทธิ์การใช้งาน'} key="2">
            {renderPermission()}
          </TabPane>
        </Tabs>
      </StickyHeader>
    )
  }

  return (
    <DetailDrawer
      title={title}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      footer={FooterContent}
      width={'50%'}
    >
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      {getTab()}
    </DetailDrawer>
  )
}

const Description = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  margin-bottom: 10px;
  span {
    font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
    color: ${(props: { theme: MyTheme }): string => props.theme.colors.drakGrey};
  }
`

const GroupTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.medium};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

const PermissionName = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.regular};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const Title = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const MerchantItem = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  padding: 8px 20px;
  cursor: pointer;
  &.active {
    border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
    border-radius: 5px;
  }
`

const PermissionBoxWrapper = styled.div`
  margin-top: 20px;
  padding: 20px 20px;
  border-radius: 5px;
  border: 1px solid rgb(238, 239, 242);
  // height: 300px;
  // overflow: scroll;
`

const SpanMerchantName = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  cursor: pointer;
  &.checked {
    color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  }
  &.not-checked {
    color: #bfbfbf;
    cursor: not-allowed;
  }
`

const SpanCount = styled(Badge)`
  span {
    color: ${(props: { theme: MyTheme }): string => props.theme.colors.white} !important;
  }
`

const CircleCheckBox = styled(Checkbox)`
  cursor: default;
  input {
    cursor: default;
  }
  span,
  label {
    border-radius: 10px;
  }
`

const StickyHeader = styled.div`
  background-color: #fff;
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectActionEvent(state),
    userDetail: selectUserDetail(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(UserDetail)
